import { Button, Checkbox, Col, Form, Input, Row } from "antd";
import { BaseButtonsForm } from "components/common/forms/BaseButtonsForm";
import { notificationsString } from "config/site.string";
import { FONT_SIZE_PX } from "config/theme/constants";
import { useEffect, useState } from "react";
import { ConfigDetails } from "redux/users/types";
import { useDispatch } from "react-redux";
import { addUpdateConfigurationDetailsRequest } from "redux/configuration/actions";
import { config } from "config/site.config";
import { notificationController } from "components/common/notificationController";
import { getGivenPermissions } from "utils/utils";
import { loadState } from "library/helpers/sessionStorage";
import { StyledButton } from "../Email/styles";

interface IConfigDetails {
  configDetails: ConfigDetails;
  onSaveCallback: () => void;
  agentType : string;
}

const Notifications = ({
  configDetails,
  onSaveCallback,
  agentType,
}: IConfigDetails) => {
  const dispatch = useDispatch();
  const [form] = BaseButtonsForm.useForm();
  const [sendFailureNotifications, setSendFailureNotifications] = useState(
    configDetails?.sendFailureNotifications || false,
  );
  const [sendSuccessNotifications, setSendSuccessNotifications] = useState(
    configDetails?.sendSuccessNotifications || false,
  );
  const [sendInvalidNotifications, setSendInvalidNotifications] = useState(
    configDetails?.sendInvalidNotifications || false,
  );
  const [sendInvoiceFailureNotifications, setSendInvoiceFailureNotifications] =
    useState(configDetails?.sendInvoiceFailureNotifications || false);
  const [sendInvoiceSuccessNotifications, setSendInvoiceSuccessNotifications] =
    useState(configDetails?.sendInvoiceSuccessNotifications || false);
  const [sendInvoiceInvalidNotifications, setSendInvoiceInvalidNotifications] =
    useState(configDetails?.sendInvoiceInvalidNotifications || false);
  const permissionsOfUser = loadState("actualPermissionsList");
  const { hasEditAccess, hasFullAccess, hasViewAccess }: any =
    getGivenPermissions(permissionsOfUser, "Configuration");
  const currentUser = JSON.parse(
    localStorage.getItem("loggedInUser") || "null",
  );

  useEffect(() => {
    form.setFieldsValue({
      sendFailureNotifications: configDetails?.sendFailureNotifications,
      failureRecipients: configDetails?.failureRecipients,
      sendSuccessNotifications: configDetails?.sendSuccessNotifications,
      successRecipients: configDetails?.successRecipients,
      sendInvalidNotifications: configDetails?.sendInvalidNotifications,
      invalidRecipients: configDetails?.invalidRecipients,
      sendInvoiceFailureNotifications:
        configDetails?.sendInvoiceFailureNotifications,
      failureInvoiceRecipients: configDetails?.failureInvoiceRecipients,
      sendInvoiceSuccessNotifications:
        configDetails?.sendInvoiceSuccessNotifications,
      successInvoiceRecipients: configDetails?.successInvoiceRecipients,
      invalidInvoiceRecipients: configDetails?.invalidInvoiceRecipients,
      sendInvoiceInvalidNotifications:
        configDetails?.sendInvoiceInvalidNotifications,

    });
    setSendFailureNotifications(configDetails?.sendFailureNotifications);
    setSendSuccessNotifications(configDetails?.sendSuccessNotifications);
    setSendInvalidNotifications(configDetails?.sendInvalidNotifications);
    setSendInvoiceFailureNotifications(
      configDetails?.sendInvoiceFailureNotifications,
    );
    setSendInvoiceSuccessNotifications(
      configDetails?.sendInvoiceSuccessNotifications,
    );
    setSendInvoiceInvalidNotifications(
      configDetails?.sendInvoiceInvalidNotifications,
    );
  }, [configDetails, form]);

  const renderInputField = (
    fieldName: string,
    fieldLabel: string,
    isRequired: boolean,
    requiredMessage: string,
    disabled?: boolean,
  ) => {
    return (
      <Col xs={24} md={24} xl={100}>
        <BaseButtonsForm.Item
          name={fieldName}
          label={<>{fieldLabel}</>}
          {...(isRequired
            ? {
                rules: [
                  {
                    required: isRequired,
                    message: `${requiredMessage} is required`,
                  },
                  {
                    validator: (_: any, value: string) => {
                      if (!value) return Promise.resolve();

                    const emails = value
                      .trim()
                      .split(";")
                      .map((e) => e?.trim())
                      .filter(Boolean);
                      
                    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

                    if (!emails.every((e) => emailRegex.test(e)))
                      return Promise.reject(
                        new Error("Please enter valid email(s) separated by semicolons"),
                      );
                      const hasDuplicates = emails.some((email, index) =>
                        emails.some(
                          (e, i) =>
                            i !== index &&
                            e.toLowerCase() === email.toLowerCase(),
                        ),
                      );

                      if (hasDuplicates)
                        return Promise.reject(
                          new Error("Duplicate email(s) are not allowed"),
                        );

                      return Promise.resolve();
                    },
                  },
                ],
              }
            : {})}
          extra={
            <div style={{ textAlign: "right" }}>
              {"(separate by a ';' if more than one recipient)"}
            </div>
          }
        >
          <Input
            type="text"
            placeholder={""}
            disabled={disabled || hasViewAccess}
          />
        </BaseButtonsForm.Item>
      </Col>
    );
  };

  const addConfigurationCallback = (type: string, message: string) => {
    if (type === "Success") {
      onSaveCallback();
      notificationController.success({
        message: "Success",
        description: "Configuration saved successfully",
        placement: "topRight",
      });
    } else {
      notificationController.error({
        message: "Error",
        description: message ? message : "Sorry an error occurred",
        placement: "topRight",
      });
    }
  };

  const handleSubmit = (values: any) => {
    const configurationValues = {
      id: configDetails?.id,
      pop3Server: configDetails?.pop3Server,
      pop3Port: configDetails?.pop3Port,
      pop3Email: configDetails?.pop3Email,
      pop3Password: configDetails?.pop3Password,
      pop3SecureSSL: configDetails?.pop3SecureSSL,
      sendFailureNotifications:
        agentType === "ORDER"
          ? values.sendFailureNotifications
          : configDetails?.sendFailureNotifications,
      failureRecipients:
        agentType === "ORDER"
          ? (values.failureRecipients?.trim() ?? "")
          : (configDetails?.failureRecipients?.trim() ?? ""),
      sendSuccessNotifications:
        agentType === "ORDER"
          ? values.sendSuccessNotifications
          : configDetails?.sendSuccessNotifications,
      successRecipients:
        agentType === "ORDER"
          ? (values.successRecipients?.trim() ?? "")
          : (configDetails?.successRecipients?.trim() ?? ""),
      sendInvalidNotifications:
        agentType === "ORDER"
          ? values.sendInvalidNotifications
          : configDetails?.sendInvalidNotifications,
      invalidRecipients:
        agentType === "ORDER"
          ? (values.invalidRecipients?.trim() ?? "")
          : (configDetails?.invalidRecipients?.trim() ?? ""),
      sendInvoiceFailureNotifications:
        agentType === "INVOICE"
          ? values.sendInvoiceFailureNotifications
          : configDetails?.sendInvoiceFailureNotifications,
      failureInvoiceRecipients:
        agentType === "INVOICE"
          ? (values.failureInvoiceRecipients?.trim() ?? "")
          : (configDetails?.failureInvoiceRecipients?.trim() ?? ""),
      sendInvoiceSuccessNotifications:
        agentType === "INVOICE"
          ? values.sendInvoiceSuccessNotifications
          : configDetails?.sendInvoiceSuccessNotifications,
      successInvoiceRecipients:
        agentType === "INVOICE"
          ? (values.successInvoiceRecipients?.trim() ?? "")
          : (configDetails?.successInvoiceRecipients?.trim() ?? ""),
      sendInvoiceInvalidNotifications:
        agentType === "INVOICE"
          ? values.sendInvoiceInvalidNotifications
          : configDetails?.sendInvoiceInvalidNotifications,
      invalidInvoiceRecipients:
        agentType === "INVOICE"
          ? (values.invalidInvoiceRecipients?.trim() ?? "")
          : (configDetails?.invalidInvoiceRecipients?.trim() ?? ""),
      updatedBy: currentUser.userDetails.userId,
    };

    dispatch(
      addUpdateConfigurationDetailsRequest(
        config.addUpdateConfiguration,
        configurationValues,
        addConfigurationCallback,
      ),
    );
  };

  // Fixed handlers - each now updates the correct state
  const handleFailedCheckboxChange = (e: any) => {
    setSendFailureNotifications(e.target.checked);
  };

  const handleSuccessCheckboxChange = (e: any) => {
    setSendSuccessNotifications(e.target.checked);
  };
  const handleInvalidCheckboxChange = (e: any) => {
    setSendInvalidNotifications(e.target.checked);
  };
  const handleInvoiceFailedCheckboxChange = (e: any) => {
    setSendInvoiceFailureNotifications(e.target.checked);
  };

  const handleInvoiceSuccessCheckboxChange = (e: any) => {
    setSendInvoiceSuccessNotifications(e.target.checked);
  };

  const handleInvoiceInvalidCheckboxChange = (e: any) => {
    setSendInvoiceInvalidNotifications(e.target.checked);
  };

  return (
    <>
      <BaseButtonsForm
        form={form}
        name="user"
        layout="horizontal"
        onFinish={handleSubmit}
        className="form-container"
        id="user"
      >
        {agentType === "ORDER" ? (
          <Row gutter={{ xs: 10, md: 15, xl: 30 }}>
            <Col xs={24} md={24}>
              <Form.Item
                name="sendFailureNotifications"
                valuePropName="checked"
              >
                <Checkbox
                  name="sendFailureNotifications"
                  className="checkboxColorClass"
                  checked={sendFailureNotifications}
                  onChange={handleFailedCheckboxChange}
                  disabled={hasViewAccess}
                >
                  {notificationsString.failure}
                </Checkbox>
              </Form.Item>
            </Col>
            {sendFailureNotifications &&
            renderInputField(
              "failureRecipients",
              notificationsString.recipients,
              true,
              notificationsString.recipients,
              false
            )}
          {!sendFailureNotifications &&
            renderInputField(
              "failureRecipients",
              notificationsString.recipients,
              false,
              notificationsString.recipients,
              true
            )}
            <Col xs={24} md={24}>
              <Form.Item
                name="sendSuccessNotifications"
                valuePropName="checked"
              >
                <Checkbox
                  name="sendSuccessNotifications"
                  className="checkboxColorClass"
                  checked={sendSuccessNotifications}
                  onChange={handleSuccessCheckboxChange}
                  disabled={hasViewAccess}
                >
                  {notificationsString.success}
                </Checkbox>
              </Form.Item>
            </Col>
         {sendSuccessNotifications &&
            renderInputField(
              "successRecipients",
              notificationsString.recipients,
              true,
              notificationsString.recipients,
              false
            )}
          {!sendSuccessNotifications &&
            renderInputField(
              "successRecipients",
              notificationsString.recipients,
              false,
              notificationsString.recipients,
              true
            )}
            <Col xs={24} md={24}>
              <Form.Item
                name="sendInvalidNotifications"
                valuePropName="checked"
              >
                <Checkbox
                  name="sendInvalidNotifications"
                  className="checkboxColorClass"
                  checked={sendInvalidNotifications}
                  onChange={handleInvalidCheckboxChange}
                  disabled={hasViewAccess}
                >
                  {notificationsString.invalid}
                </Checkbox>
              </Form.Item>
            </Col>
         {sendInvalidNotifications &&
            renderInputField(
              "invalidRecipients",
              notificationsString.recipients,
              true,
              notificationsString.recipients,
              false
            )}
          {!sendInvalidNotifications &&
            renderInputField(
              "invalidRecipients",
              notificationsString.recipients,
              false,
              notificationsString.recipients,
              true
            )}
          </Row>
        ) : (
          <Row gutter={{ xs: 10, md: 15, xl: 30 }}>
            <Col xs={24} md={24}>
              <Form.Item
                name="sendInvoiceFailureNotifications"
                valuePropName="checked"
              >
                <Checkbox
                  name="sendInvoiceFailureNotifications"
                  className="checkboxColorClass"
                  checked={sendInvoiceFailureNotifications}
                  onChange={handleInvoiceFailedCheckboxChange}
                  disabled={hasViewAccess}
                >
                  {notificationsString.failure}
                </Checkbox>
              </Form.Item>
            </Col>
            {sendInvoiceFailureNotifications &&
            renderInputField(
              "failureInvoiceRecipients",
              notificationsString.recipients,
              true,
              notificationsString.recipients,
              false
            )}
          {!sendInvoiceFailureNotifications &&
            renderInputField(
              "failureInvoiceRecipients",
              notificationsString.recipients,
              false,
              notificationsString.recipients,
              true
            )}
            <Col xs={24} md={24}>
              <Form.Item
                name="sendInvoiceSuccessNotifications"
                valuePropName="checked"
              >
                <Checkbox
                  name="sendInvoiceSuccessNotifications"
                  className="checkboxColorClass"
                  checked={sendInvoiceSuccessNotifications}
                  onChange={handleInvoiceSuccessCheckboxChange}
                  disabled={hasViewAccess}
                >
                  {notificationsString.success}
                </Checkbox>
              </Form.Item>
            </Col>
           {sendInvoiceSuccessNotifications &&
            renderInputField(
              "successInvoiceRecipients",
              notificationsString.recipients,
              true,
              notificationsString.recipients,
              false
            )}
          {!sendInvoiceSuccessNotifications &&
            renderInputField(
              "successInvoiceRecipients",
              notificationsString.recipients,
              false,
              notificationsString.recipients,
              true
            )}
            <Col xs={24} md={24}>
              <Form.Item
                name="sendInvoiceInvalidNotifications"
                valuePropName="checked"
              >
                <Checkbox
                  name="sendInvoiceInvalidNotifications"
                  className="checkboxColorClass"
                  checked={sendInvoiceInvalidNotifications}
                  onChange={handleInvoiceInvalidCheckboxChange}
                  disabled={hasViewAccess}
                >
                  {notificationsString.invalid}
                </Checkbox>
              </Form.Item>
            </Col>
           {sendInvoiceInvalidNotifications &&
            renderInputField(
              "invalidInvoiceRecipients",
              notificationsString.recipients,
              true,
              notificationsString.recipients,
              false
            )}
          {!sendInvoiceInvalidNotifications &&
            renderInputField(
              "invalidInvoiceRecipients",
              notificationsString.recipients,
              false,
              notificationsString.recipients,
              true
            )}
            </Row>
        )}
        {(hasEditAccess || hasFullAccess) && (
          <Row gutter={{ xs: 10, md: 15, xl: 30 }}>
            <Col span={6}>
              <StyledButton>
                <Button
                  key="submit"
                  type="primary"
                  htmlType="submit"
                  style={{
                    fontSize: FONT_SIZE_PX.xs,
                    marginRight: 19,
                    minWidth: 100,
                  }}
                >
                  SAVE
                </Button>
              </StyledButton>
            </Col>
          </Row>
        )}
      </BaseButtonsForm>
    </>
  );
};

export default Notifications;
